/**
 * 项目物料页面
 *
 * 功能说明：
 * 1. 顶部项目选择器 - 切换项目加载对应物料数据
 * 2. 筛选区域 - 使用 SearchFilter 组件统一筛选
 * 3. 操作区域 - 刷新 + 导出数据
 * 4. 数据表格 - 展示物料13列信息 + 审核记录操作列
 * 5. 审核记录弹窗 - 查看物料在当前项目下的审核历史
 *
 * 对应原型：物料管理 > 项目物料
 */
import React from 'react'
import { Button, Select, Modal } from 'antd'
import Search from '@/components/SearchFilter'
import TableAntd from '@/components/TableAntd'
import Buttonbar from '@/components/ButtonBar'
import useHook, { Props } from './hooks'
import { THIRD_ON_SHELF_MAP } from '@/api/projectMaterialApi'
import Style from './style.module.scss'
import moment from 'moment'

const { Option } = Select

const ProjectMaterial: React.FC<Props> = (props) => {
  const {
    state,
    handleProjectChange,
    handleSearch,
    handlePaginationChange,
    handleRefresh,
    handleExport,
    handleViewAuditRecord,
    handleCloseAuditDrawer,
  } = useHook(props)

  const {
    projectList,
    selectedProjectCode,
    dataSource,
    isTableLoading,
    pagination,
    auditDrawerVisible,
    currentAuditMaterial,
    auditRecords,
    isAuditLoading,
  } = state

  // ======================== 表格列定义 ========================

  /** 主列表 - 列配置（字段名对应 ProjectMaterialDTO） */
  const listColumn = [
    {
      key: 'productCode',
      title: '咸亨SKU编码',
      width: 130,
      fixed: 'left',
    },
    {
      key: 'customerSkuCode',
      title: '客户SKU编码',
      width: 140,
    },
    {
      key: 'customerSpuCode',
      title: '客户SPU编码',
      width: 140,
    },
    {
      key: 'customerMaterialCode',
      title: '客户物料编码',
      width: 130,
    },
    {
      key: 'customerMaterialUnit',
      title: '客户物资编码单位',
      width: 140,
    },
    {
      key: 'thirdOnShelf',
      title: '客户平台状态',
      width: 120,
      render: (text: string) => {
        if (!text) return '-'
        /** 状态值 → 样式类名映射：1已上架(绿)、2已下架(红)，其他默认 */
        const styleMap: Record<string, string> = {
          '1': Style['status-tag-listed'],
          '2': Style['status-tag-delisted'],
        }
        return (
          <span className={styleMap[text] || ''}>
            {THIRD_ON_SHELF_MAP[text] || text}
          </span>
        )
      },
    },
    {
      key: 'thirdOnShelfTime',
      title: '状态变更时间',
      width: 160,
    },
    {
      key: 'pushPrice',
      title: '推送协议价',
      width: 110,
      render: (text: string) =>
        text ? <span className={Style['price-text']}>¥{text}</span> : '-',
    },
    {
      key: 'brandName',
      title: '品牌',
      width: 100,
    },
    {
      key: 'productName',
      title: '名称',
      width: 120,
    },
    {
      key: 'manuCode',
      title: '规格/型号',
      width: 120,
    },
    {
      key: 'unitName',
      title: '基本单位',
      width: 80,
    },
    {
      key: 'description',
      title: '描述',
      width: 200,
    },
    {
      key: 'auditRecord',
      title: '审核记录',
      fixed: 'right',
      width: 100,
      render: (_: any, record: any) => (
        <Button type="link" onClick={() => handleViewAuditRecord(record)}>
          查看审核
        </Button>
      ),
    },
  ]

  /** 审核记录弹框 - 列配置（字段名对应 ProjectMaterialAuditRecordDTO） */
  const auditColumns = [
    {
      key: 'checkTypeDesc',
      title: '审核类型',
      width: 180,
    },
    {
      key: 'checkResultDesc',
      title: '审核结果',
      width: 100,
    },
    {
      key: 'checkTime',
      title: '审核时间',
      width: 180,
      render: (text: number) =>
        text ? moment(text).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      key: 'remark',
      title: '审核备注',
    },
  ]

  // ======================== 渲染 ========================

  return (
    <div className={Style['container']}>
      {/* ========== 1. 项目选择器 ========== */}
      <div className={Style['project-selector']}>
        <span className={Style['project-label']}>项目名称：</span>
        <Select
          style={{ width: 220 }}
          value={selectedProjectCode || undefined}
          placeholder="请选择项目"
          onChange={handleProjectChange}
          allowClear
          showSearch
          filterOption={(input, option) =>
            (option?.children as unknown as string)
              ?.toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          {projectList.map((project) => (
            <Option key={project.projectCode} value={project.projectCode}>
              {project.projectName}
            </Option>
          ))}
        </Select>
        <span className={Style['project-hint']}>
          * 请先选择项目以加载对应物料数据
        </span>
      </div>

      {/* ========== 2. 筛选区域 - 使用 SearchFilter 组件 ========== */}
      <Search
        fieldsData={[
          'productCodes',
          'customerSkuCodes',
          'customerSpuCodes',
          'customerMaterialCodes',
          'thirdOnShelf',
          'thirdOnShelfStartTime',
          'thirdOnShelfEndTime',
          'productName',
        ]}
        onSearch={handleSearch}
        keyName="PROJECTMATERIAL"
        ScreenKey="PROJECTMATERIAL"
        hideScheme
      />

      {/* ========== 3. 操作栏 + 表格 ========== */}
      <div className={Style['inner-content']}>
        <Buttonbar onRefresh={handleRefresh}>
          <Button type="primary" onClick={handleExport}>
            导出数据
          </Button>
        </Buttonbar>

        <TableAntd
          dataSource={dataSource}
          listColumn={listColumn}
          loading={isTableLoading}
          Y={380}
          X={1800}
          pagination={{
            ...pagination,
            onChange: handlePaginationChange,
          }}
          showSize
        />
      </div>

      {/* ========== 4. 审核记录弹框 ========== */}
      <Modal
        title={
          <span>
            审核记录{' '}
            {currentAuditMaterial && (
              <span style={{ color: '#ff4d4f' }}>
                ({currentAuditMaterial.productCode})
              </span>
            )}
          </span>
        }
        open={auditDrawerVisible}
        onCancel={handleCloseAuditDrawer}
        footer={null}
        width={950}
        destroyOnClose
      >
        {/* 副标题提示 */}
        <div className={Style['audit-modal-hint']}>
          展示当前物料最新审核记录信息
        </div>

        {/* 审核记录表格 - 不分页，固定高度滚动 */}
        <div className={Style['audit-table-wrapper']}>
          <TableAntd
            dataSource={auditRecords}
            listColumn={auditColumns}
            loading={isAuditLoading}
            Y={400}
          />
        </div>
      </Modal>
    </div>
  )
}

export default ProjectMaterial
/**
 * 项目物料页面
 *
 * 功能说明：
 * 1. 顶部项目选择器 - 切换项目加载对应物料数据
 * 2. 筛选区域 - 使用 SearchFilter 组件统一筛选
 * 3. 操作区域 - 刷新 + 导出数据
 * 4. 数据表格 - 展示物料13列信息 + 审核记录操作列
 * 5. 审核记录弹窗 - 查看物料在当前项目下的审核历史
 *
 * 对应原型：物料管理 > 项目物料
 */
import React from 'react'
import { Button, Select, Modal } from 'antd'
import Search from '@/components/SearchFilter'
import TableAntd from '@/components/TableAntd'
import Buttonbar from '@/components/ButtonBar'
import useHook, { Props } from './hooks'
import { THIRD_ON_SHELF_MAP } from '@/api/projectMaterialApi'
import Style from './style.module.scss'
import moment from 'moment'

const { Option } = Select

const ProjectMaterial: React.FC<Props> = (props) => {
  const {
    state,
    handleProjectChange,
    handleSearch,
    handlePaginationChange,
    handleRefresh,
    handleExport,
    handleViewAuditRecord,
    handleCloseAuditDrawer,
  } = useHook(props)

  const {
    projectList,
    selectedProjectCode,
    dataSource,
    isTableLoading,
    pagination,
    auditDrawerVisible,
    currentAuditMaterial,
    auditRecords,
    isAuditLoading,
  } = state

  // ======================== 表格列定义 ========================

  /** 主列表 - 列配置（字段名对应 ProjectMaterialDTO） */
  const listColumn = [
    {
      key: 'productCode',
      title: '咸亨SKU编码',
      width: 130,
      fixed: 'left',
    },
    {
      key: 'customerSkuCode',
      title: '客户SKU编码',
      width: 140,
    },
    {
      key: 'customerSpuCode',
      title: '客户SPU编码',
      width: 140,
    },
    {
      key: 'customerMaterialCode',
      title: '客户物料编码',
      width: 130,
    },
    {
      key: 'customerMaterialUnit',
      title: '客户物资编码单位',
      width: 140,
    },
    {
      key: 'thirdOnShelf',
      title: '客户平台状态',
      width: 120,
      render: (text: string) => {
        if (!text) return '-'
        /** 状态值 → 样式类名映射：1已上架(绿)、2已下架(红)，其他默认 */
        const styleMap: Record<string, string> = {
          '1': Style['status-tag-listed'],
          '2': Style['status-tag-delisted'],
        }
        return (
          <span className={styleMap[text] || ''}>
            {THIRD_ON_SHELF_MAP[text] || text}
          </span>
        )
      },
    },
    {
      key: 'thirdOnShelfTime',
      title: '状态变更时间',
      width: 160,
    },
    {
      key: 'pushPrice',
      title: '推送协议价',
      width: 110,
      render: (text: string) =>
        text ? <span className={Style['price-text']}>¥{text}</span> : '-',
    },
    {
      key: 'brandName',
      title: '品牌',
      width: 100,
    },
    {
      key: 'productName',
      title: '名称',
      width: 120,
    },
    {
      key: 'manuCode',
      title: '规格/型号',
      width: 120,
    },
    {
      key: 'unitName',
      title: '基本单位',
      width: 80,
    },
    {
      key: 'description',
      title: '描述',
      width: 200,
    },
    {
      key: 'auditRecord',
      title: '审核记录',
      fixed: 'right',
      width: 100,
      render: (_: any, record: any) => (
        <Button type="link" onClick={() => handleViewAuditRecord(record)}>
          查看审核
        </Button>
      ),
    },
  ]

  /** 审核记录弹框 - 列配置（字段名对应 ProjectMaterialAuditRecordDTO） */
  const auditColumns = [
    {
      key: 'checkTypeDesc',
      title: '审核类型',
      width: 180,
    },
    {
      key: 'checkResultDesc',
      title: '审核结果',
      width: 100,
    },
    {
      key: 'checkTime',
      title: '审核时间',
      width: 180,
      render: (text: number) =>
        text ? moment(text).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      key: 'remark',
      title: '审核备注',
    },
  ]

  // ======================== 渲染 ========================

  return (
    <div className={Style['container']}>
      {/* ========== 1. 项目选择器 ========== */}
      <div className={Style['project-selector']}>
        <span className={Style['project-label']}>项目名称：</span>
        <Select
          style={{ width: 220 }}
          value={selectedProjectCode || undefined}
          placeholder="请选择项目"
          onChange={handleProjectChange}
          allowClear
          showSearch
          filterOption={(input, option) =>
            (option?.children as unknown as string)
              ?.toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          {projectList.map((project) => (
            <Option key={project.projectCode} value={project.projectCode}>
              {project.projectName}
            </Option>
          ))}
        </Select>
        <span className={Style['project-hint']}>
          * 请先选择项目以加载对应物料数据
        </span>
      </div>

      {/* ========== 2. 筛选区域 - 使用 SearchFilter 组件 ========== */}
      <Search
        fieldsData={[
          'productCodes',
          'customerSkuCodes',
          'customerSpuCodes',
          'customerMaterialCodes',
          'thirdOnShelf',
          'thirdOnShelfStartTime',
          'thirdOnShelfEndTime',
          'productName',
        ]}
        onSearch={handleSearch}
        keyName="PROJECTMATERIAL"
        ScreenKey="PROJECTMATERIAL"
        hideScheme
      />

      {/* ========== 3. 操作栏 + 表格 ========== */}
      <div className={Style['inner-content']}>
        <Buttonbar onRefresh={handleRefresh}>
          <Button type="primary" onClick={handleExport}>
            导出数据
          </Button>
        </Buttonbar>

        <TableAntd
          dataSource={dataSource}
          listColumn={listColumn}
          loading={isTableLoading}
          Y={380}
          X={1800}
          pagination={{
            ...pagination,
            onChange: handlePaginationChange,
          }}
          showSize
        />
      </div>

      {/* ========== 4. 审核记录弹框 ========== */}
      <Modal
        title={
          <span>
            审核记录{' '}
            {currentAuditMaterial && (
              <span style={{ color: '#ff4d4f' }}>
                ({currentAuditMaterial.productCode})
              </span>
            )}
          </span>
        }
        open={auditDrawerVisible}
        onCancel={handleCloseAuditDrawer}
        footer={null}
        width={950}
        destroyOnClose
      >
        {/* 副标题提示 */}
        <div className={Style['audit-modal-hint']}>
          展示当前物料最新审核记录信息
        </div>

        {/* 审核记录表格 - 不分页，固定高度滚动 */}
        <div className={Style['audit-table-wrapper']}>
          <TableAntd
            dataSource={auditRecords}
            listColumn={auditColumns}
            loading={isAuditLoading}
            Y={400}
          />
        </div>
      </Modal>
    </div>
  )
}

export default ProjectMaterial
